import extractDomain from 'extract-domain';
import Cookies from 'js-cookie';

const KEY = 'affiliation';
const EXPIRY_DAYS = 90;

const cookieDomain = window.location.hostname.includes('.') ? '.' + extractDomain(window.location.hostname) : window.location.hostname;

export const retrieveParams = () => {
  const currentParams = extractParams();

  if (currentParams) {
    saveParams(currentParams);
    return currentParams;
  }

  return retrieveSavedParams();
};

export const getFacebookBrowserId = () => {
  try {
    if (typeof document === 'undefined' || typeof Cookies === 'undefined') return null;
    const fbpCookie = Cookies.get('_fbp');
    return fbpCookie || null;
  } catch (error) {
    console.warn('Error reading Facebook _fbp cookie:', error);
    return null;
  }
};

/**
 * Extract GA4 Client ID from the main _ga cookie
 * @returns Client ID string or null if not found
 */
export function getGA4ClientId() {
  try {
    if (typeof document === 'undefined') return null;
    if (!document.cookie) return null;
    
    // Extract Client ID from the main _ga cookie (used for both UA and GA4)
    const match = document.cookie.match(/_ga=GA1\.\d+\.(.+?)(?:;|$)/);
    return match ? match[1] : null;
  } catch (error) {
    console.warn('Error reading GA4 Client ID:', error);
    return null;
  }
}

/**
 * Extract GA4 Session data from GA4-specific cookies (_ga_MEASUREMENT_ID)
 * Supports both GS1 and GS2 formats
 * @returns Object with sessionId and sessionNumber, or null if not found
 */
export function getGA4SessionData() {
  try {
    if (typeof document === 'undefined') return null;
    if (!document.cookie) return null;
    
    const cookies = document.cookie.split(';');
    
    // Look for GA4 cookies that match the pattern _ga_XXXXXXXXXX
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      const ga4Match = trimmed.match(/^_ga_([A-Z0-9]+)=(.+)$/);
      
      if (ga4Match) {
        const cookieValue = ga4Match[2];
        
        // Handle GS2 format (newer format with prefixes)
        if (cookieValue.startsWith('GS2.1.')) {
          const data = cookieValue.substring(6); // Remove "GS2.1."
          const segments = data.replace(/%24/g, '$').split('$');
          
          let sessionId = '';
          let sessionNumber = '';
          
          for (const segment of segments) {
            if (segment.startsWith('s')) {
              sessionId = segment.substring(1);
            } else if (segment.startsWith('o')) {
              sessionNumber = segment.substring(1);
            }
          }
          
          if (sessionId && sessionNumber) {
            return { sessionId, sessionNumber };
          }
        }
        
        // Handle GS1 format (older format with dot separation)
        else if (cookieValue.startsWith('GS1.1.')) {
          const parts = cookieValue.split('.');
          if (parts.length >= 4) {
            return {
              sessionId: parts[2],
              sessionNumber: parts[3]
            };
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading GA4 Session data:', error);
    return null;
  }
}

const extractParams = () => {
  const params = new URLSearchParams(window.location.search);
  const trackingParams = {
    source: (params.get('utm_source') || '').toLowerCase(),
    code: null,
    query: window.location.search,
  };

  if (params.get('gclid')) {
    trackingParams.source = 'google';
    trackingParams.code = params.get('gclid');
    
    // For Google: Use GA4 Session ID as browserId (with error handling)
    try {
      const ga4SessionData = getGA4SessionData();
      if (ga4SessionData && ga4SessionData.sessionId) {
        trackingParams.browserId = ga4SessionData.sessionId;
      }
    } catch (error) {
      console.warn('Error extracting Google GA4 Session ID:', error);
    }

    return trackingParams;
  }

  if (params.get('fbclid')) {
    trackingParams.source = 'facebook';
    trackingParams.code = params.get('fbclid');
    
    // For Facebook: Use _fbp cookie as browserId (with error handling)
    try {
      const fbpCookie = getFacebookBrowserId();
      if (fbpCookie) {
        trackingParams.browserId = fbpCookie;
      }
    } catch (error) {
      console.warn('Error extracting Facebook _fbp cookie:', error);
    }
    
    // Add GA4 tracking data (with error handling)
    try {
      const ga4ClientId = getGA4ClientId();
      const ga4SessionData = getGA4SessionData();
      
      if (ga4ClientId) {
        trackingParams.ga4ClientId = ga4ClientId;
      }
      if (ga4SessionData && ga4SessionData.sessionId) {
        trackingParams.ga4SessionId = ga4SessionData.sessionId;
      }
    } catch (error) {
      console.warn('Error extracting GA4 data for Facebook:', error);
    }

    return trackingParams;
  }

  switch (trackingParams.source) {
    case 'affiliate':
      trackingParams.source = 'adservice';
      trackingParams.code = params.get('coid');
      return trackingParams;
    case 'adtraction':
      trackingParams.code = params.get('at_gd');
      return trackingParams;
    case 'ilona':
      trackingParams.code = params.get('utm_campaign');
      return trackingParams;
    default:
      return null;
  }
};

const saveParams = (value) => {
  Cookies.set(KEY, JSON.stringify(value), { expires: EXPIRY_DAYS, path: '/', domain: cookieDomain });
};

const retrieveSavedParams = () => {
  if (Cookies.get(KEY)) {
    return JSON.parse(Cookies.get(KEY));
  }
  return null;
};